'use client';

import React from 'react';

import { LibraryIcon, LibraryIconSymbol } from '@repo/icons/library-icon';
import { useIntersection } from 'react-use';

import { Button } from '~/components/ui/button';
import { Input } from '~/components/ui/input';
import { EMAIL_COLLECTION_ENDPOINT } from '~/lib/constants';
import { cn } from '~/lib/utils';
import { BlockEmailCollectionFragment } from '~/server/generated/contentful-graphql';

export function EmailCollection({ block }: { block: BlockEmailCollectionFragment }) {
  const { separator, urlEndpoint, formId, copy, heading, icon, ctaTitle } = block;

  const [slug, setSlug] = React.useState<string>('');
  const [honeypot, setHoneypot] = React.useState(false);
  const [animate, setAnimate] = React.useState(false);

  const formRef = React.useRef<HTMLDivElement>(null!);

  const intersection = useIntersection(formRef, {
    root: null,
    rootMargin: '0px',
    threshold: 1,
  });
  const fullyInView = intersection && intersection.intersectionRatio >= 0.8;

  React.useEffect(() => {
    if (fullyInView) {
      setAnimate(true);
    }
  }, [fullyInView]);

  React.useEffect(() => {
    setSlug(window.location.pathname);
  }, []);

  if (!block) return null;

  return (
    <section className={cn('container xl:max-w-7xl', separator === 'Default' && 'separator w-full')}>
      <div
        className='ease bg-teal flex origin-bottom flex-col rounded-2xl px-5 pb-14 pt-6 duration-500 sm:pt-14'
        ref={formRef}
      >
        <div className='mx-auto w-full max-w-5xl'>
          <header className='flex flex-col md:items-center'>
            {icon && (
              <LibraryIcon
                className={cn('text-yellow mb-6', animate && 'animate-wiggle')}
                height={40}
                use={icon as LibraryIconSymbol}
                width={60}
              />
            )}
            <h2 className='heading-base mb-3 text-white'>{heading}</h2>
            <p className='body-lg mx-auto  text-center text-white'>{copy}</p>
          </header>
          <form action={urlEndpoint ? urlEndpoint : EMAIL_COLLECTION_ENDPOINT} className='dark mt-8' method='post'>
            <fieldset className='m-0 flex grow flex-col border-none p-0 pb-2'>
              <div className='mb-6 grid gap-6 lg:grid-cols-3'>
                {/* Honeypot */}
                <div hidden>
                  <label htmlFor='g0tcha'> g0tcha</label>
                  <input
                    aria-label='g0tcha'
                    name='g0tcha'
                    onChange={(event) => setHoneypot(!!event.target.value)}
                    type='text'
                  />
                </div>
                {/* Pardot hidden form fields START */}
                <div hidden>
                  <label htmlFor='form_id'>Form ID</label>
                  <Input
                    defaultValue={`email-collection-${formId ? formId : 'general'}`}
                    name='form_id'
                    readOnly
                    type='text'
                  />
                </div>
                <div hidden>
                  <label htmlFor='slug'>Slug</label>
                  <Input defaultValue={slug} name='slug' readOnly type='text' />
                </div>
                {/* Pardot hidden form fields END */}
                <div>
                  <label className='sr-only' htmlFor='first_name'>
                    First Name
                  </label>
                  <Input name='first_name' id='first_name' placeholder='First name' required type='text' />
                </div>
                <div>
                  <label className='sr-only' htmlFor='last_name'>
                    Last Name
                  </label>
                  <Input name='last_name' id='last_name' placeholder='Last name' required type='text' />
                </div>
                <div>
                  <label className='sr-only'>Your Email</label>
                  <Input name='email' id='email' placeholder='Your email' required type='text' />
                </div>
              </div>
              <Button className='text-teal w-full rounded-lg' disabled={honeypot} type='submit' variant='inverted'>
                {ctaTitle}
              </Button>
            </fieldset>
          </form>
        </div>
      </div>
    </section>
  );
}
